import { FC, useMemo } from 'react';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';

import { getDocumentConceptPalette } from './documentConceptPalette';
import { DocumentConceptReference, DocumentIntelligence } from './documentIntelligence';

interface DocumentConceptOverlayProps {
    activeReferenceId?: string;
    currentPage: number;
    enabled: boolean;
    intelligence: DocumentIntelligence;
    onReferenceSelect: (reference: DocumentConceptReference) => void;
}

const DocumentConceptOverlay: FC<DocumentConceptOverlayProps> = ({
    activeReferenceId,
    currentPage,
    enabled,
    intelligence,
    onReferenceSelect,
}) => {
    const { token } = useAntDesignTheme();
    const references = useMemo(
        () =>
            intelligence.concepts.flatMap((concept) =>
                concept.references
                    .filter((reference) => reference.page === currentPage)
                    .map((reference) => ({ concept, reference })),
            ),
        [currentPage, intelligence.concepts],
    );

    if (!enabled || !references.length) {
        return null;
    }

    return (
        <div
            aria-label={`Concept highlights for page ${currentPage}`}
            style={{ inset: 0, pointerEvents: 'none', position: 'absolute' }}
        >
            {references.flatMap(({ concept, reference }) => {
                const active = activeReferenceId === reference.id;
                const domain = concept.primaryDomainId
                    ? intelligence.domains.find((candidate) => candidate.id === concept.primaryDomainId)
                    : undefined;
                const domainLabel = domain?.label ?? 'Document-Specific';
                const palette = getDocumentConceptPalette(token, concept, domain?.order);
                const rectangles = reference.rectangles ?? [reference];

                return rectangles.map((rectangle, index) => (
                    <button
                        key={`${reference.id}:${index}`}
                        aria-label={`Show ${concept.label} reference on page ${reference.page}`}
                        data-concept-id={concept.id}
                        data-domain-id={concept.primaryDomainId ?? 'document-specific'}
                        data-reference-id={reference.id}
                        title={`${concept.label} · ${domainLabel}`}
                        type='button'
                        onClick={() => onReferenceSelect(reference)}
                        style={{
                            background: active ? palette.activeBackground : palette.background,
                            border: `${active ? 2 : 1}px solid ${palette.border}`,
                            borderRadius: token.borderRadiusSM,
                            boxShadow: active
                                ? `0 0 0 2px ${token.colorBgContainer}, 0 0 0 4px ${palette.border}`
                                : undefined,
                            boxSizing: 'border-box',
                            cursor: 'pointer',
                            height: `${rectangle.height * 100}%`,
                            left: `${rectangle.x * 100}%`,
                            opacity: active ? 0.82 : 0.58,
                            padding: 0,
                            pointerEvents: 'auto',
                            position: 'absolute',
                            top: `${rectangle.y * 100}%`,
                            width: `${rectangle.width * 100}%`,
                            zIndex: active ? 6 : 5,
                        }}
                    />
                ));
            })}
        </div>
    );
};

export default DocumentConceptOverlay;
